import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PlatformLayout from '../../components/layout/PlatformLayout';
import './Platform.css';
import { Bell, RefreshCw, Check, X, AlertTriangle, PhoneOff } from 'lucide-react';

/**
 * Platform admin → Push log (migrate-v25). Every push attempt is recorded so
 * "did the parent get the cancellation?" is answerable in under a minute.
 * Failed / undelivered sends can be replayed straight from the row.
 */

const STATUS_META = {
  sent:      { label: 'Delivered', color: '#1E4A35', icon: Check },
  failed:    { label: 'Failed',    color: '#A33',    icon: X },
  pruned:    { label: 'Dead token', color: '#C4943A', icon: AlertTriangle },
  no_tokens: { label: 'No device', color: '#888',    icon: PhoneOff },
};

const FILTERS = ['all', 'sent', 'failed', 'pruned', 'no_tokens'];

export default function PlatformPush() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('all');
  const [eventId, setEventId] = useState('');
  const [flash, setFlash] = useState(null); // { id, ok, msg }

  const { data: rows = [], isLoading, isError, error } = useQuery({
    queryKey: ['platform-push-log', status, eventId],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (status !== 'all') qs.set('status', status);
      if (eventId.trim()) qs.set('eventId', eventId.trim());
      qs.set('limit', '200');
      return axios.get(`/push/log?${qs.toString()}`).then(r => r.data);
    },
  });

  const replay = useMutation({
    mutationFn: (id) => axios.post(`/push/log/${id}/replay`).then(r => r.data),
    onSuccess: (data, id) => {
      const ok = (data.sent || 0) > 0;
      // B-38 item 4: nothing is pushed on behalf of a suspended club.
      const msg = data.suppressed === 'club_suspended'
        ? 'Club is paused — not sent'
        // B-88: the person switched this kind of notification off.
        : data.optedOut ? 'They switched this kind off — not sent'
        : ok ? 'Re-sent ✓' : `No delivery (${data.tokens || 0} device${data.tokens === 1 ? '' : 's'})`;
      setFlash({ id, ok, msg });
      setTimeout(() => setFlash(f => (f?.id === id ? null : f)), 3500);
      qc.invalidateQueries({ queryKey: ['platform-push-log'] });
    },
    onError: (err, id) => {
      setFlash({ id, ok: false, msg: err?.response?.data?.error || 'Replay failed' });
      setTimeout(() => setFlash(f => (f?.id === id ? null : f)), 3500);
    },
  });

  const fmtTime = (ts) =>
    new Date(ts).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <PlatformLayout>
      <div className="platform-page">
        <div className="page-header">
          <div>
            <h1>Push log</h1>
            <p>Every notification attempt, newest first. Filter by an event ID to confirm a fixture or cancellation reached everyone.</p>
          </div>
          <div className="period-selector">
            {FILTERS.map(s => (
              <button
                key={s}
                className={`period-btn ${status === s ? 'active' : ''}`}
                onClick={() => setStatus(s)}
              >
                {s === 'all' ? 'All' : (STATUS_META[s]?.label || s)}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <input
            value={eventId}
            onChange={e => setEventId(e.target.value)}
            placeholder="Filter by event ID (paste a fixture/event UUID)…"
            style={{
              width: '100%', maxWidth: 460, boxSizing: 'border-box', fontSize: 13.5,
              padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.15)', fontFamily: 'inherit',
            }}
          />
        </div>

        {isLoading ? (
          <p style={{ color: '#888' }}>Loading…</p>
        ) : isError ? (
          <p style={{ color: '#A33' }}>Couldn’t load the push log: {error?.response?.data?.error || error?.message}</p>
        ) : rows.length === 0 ? (
          <p style={{ color: '#888', padding: '24px 0' }}>
            No push attempts {status !== 'all' ? `with status “${STATUS_META[status]?.label}” ` : ''}
            {eventId.trim() ? 'for that event ' : ''}yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map(r => {
              const meta = STATUS_META[r.status] || STATUS_META.failed;
              const Icon = meta.icon;
              const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email || '(deleted user)';
              const canReplay = !!r.user_id; // need a user to replay to
              const busy = replay.isPending && replay.variables === r.id;
              return (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                  background: '#fff', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)', flexWrap: 'wrap',
                }}>
                  <Icon size={16} style={{ color: meta.color, flexShrink: 0 }} title={meta.label} />
                  <span style={{ fontSize: 12.5, color: '#888', whiteSpace: 'nowrap', minWidth: 92 }}>
                    {fmtTime(r.created_at)}
                  </span>
                  <span style={{ flex: 1, minWidth: 180 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#2C2B28' }}>{r.title}</span>
                    {r.body ? <span style={{ fontSize: 12.5, color: '#888', display: 'block' }}>{r.body}</span> : null}
                    {r.error ? <span style={{ fontSize: 12, color: '#A33', display: 'block' }}>{r.error}</span> : null}
                  </span>
                  <span style={{ fontSize: 12.5, color: '#555', whiteSpace: 'nowrap' }}>
                    {name}
                    {r.notif_type ? <span style={{ color: '#aaa' }}> · {r.notif_type}</span> : null}
                  </span>
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                    background: `${meta.color}22`, color: meta.color, whiteSpace: 'nowrap',
                  }}>
                    {meta.label}
                  </span>
                  {canReplay ? (
                    <button
                      className="period-btn"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px' }}
                      disabled={busy}
                      onClick={() => replay.mutate(r.id)}
                      title="Re-send this notification to the same member"
                    >
                      <RefreshCw size={13} />
                      {busy ? 'Sending…' : 'Replay'}
                    </button>
                  ) : null}
                  {flash?.id === r.id && (
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: flash.ok ? '#1E4A35' : '#A33', whiteSpace: 'nowrap' }}>
                      {flash.msg}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
